import { ContextualMarker, ViewportInfo } from './types'

export class ContextualFeedbackManager {
  private isEnabled = false
  private markers: Map<string, ContextualMarker> = new Map()
  private overlay: HTMLElement | null = null
  private instructionBanner: HTMLElement | null = null
  private onMarkerClick: ((marker: ContextualMarker) => void) | null = null

  constructor() {
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleEscapeKey = this.handleEscapeKey.bind(this)
  }

  enable(onMarkerClick: (marker: ContextualMarker) => void) {
    if (this.isEnabled) return

    console.log('FeedLet: Contextual feedback enabled')
    this.isEnabled = true
    this.onMarkerClick = onMarkerClick
    this.createOverlay()
    this.showInstructions()
    
    document.addEventListener('click', this.handleDocumentClick, true)
    document.addEventListener('keydown', this.handleEscapeKey)
    document.body.style.cursor = 'crosshair'
    
    // Add a subtle overlay to indicate contextual mode
    document.body.classList.add('feedlet-contextual-mode')
  }

  disable() {
    if (!this.isEnabled) return

    console.log('FeedLet: Disabling contextual feedback mode')
    this.isEnabled = false
    this.onMarkerClick = null
    this.clearMarkers()
    this.removeOverlay()
    this.hideInstructions()
    
    document.removeEventListener('click', this.handleDocumentClick, true)
    document.removeEventListener('keydown', this.handleEscapeKey)
    document.body.style.cursor = ''
    
    document.body.classList.remove('feedlet-contextual-mode')
    console.log('FeedLet: Contextual feedback mode disabled')
  }

  private createOverlay() {
    this.overlay = document.createElement('div')
    this.overlay.id = 'feedlet-contextual-overlay'
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.02);
      z-index: 999998;
      pointer-events: none;
      backdrop-filter: blur(0.5px);
    `
    document.body.appendChild(this.overlay)
  }

  private removeOverlay() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }
  }

  private showInstructions() {
    this.instructionBanner = document.createElement('div')
    this.instructionBanner.id = 'feedlet-instruction-banner'
    this.instructionBanner.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #1f2937;
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 8px;
        animation: feedlet-slide-down 0.3s ease-out;
      ">
        <span style="font-size: 16px;">💬</span>
        Click anywhere to add feedback
        <button id="feedlet-exit-contextual" style="
          background: #374151;
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          margin-left: 8px;
        ">
          Exit
        </button>
      </div>
    `

    // Add keyframe animation
    if (!document.getElementById('feedlet-contextual-styles')) {
      const style = document.createElement('style')
      style.id = 'feedlet-contextual-styles'
      style.textContent = `
        @keyframes feedlet-slide-down {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        
        .feedlet-contextual-mode * {
          cursor: crosshair !important;
        }
        
        .feedlet-marker {
          position: absolute;
          width: 24px;
          height: 24px;
          background: #3b82f6;
          border: 2px solid white;
          border-radius: 50%;
          cursor: pointer;
          z-index: 999997;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transition: transform 0.2s ease;
        }
        
        .feedlet-marker:hover {
          transform: scale(1.1);
        }
        
        .feedlet-marker::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          transform: translate(-50%, -50%);
        }
      `
      document.head.appendChild(style)
    }

    document.body.appendChild(this.instructionBanner)

    // Add exit button click handler
    const exitButton = document.getElementById('feedlet-exit-contextual')
    if (exitButton) {
      exitButton.addEventListener('click', () => this.disable())
    }
  }

  private hideInstructions() {
    if (this.instructionBanner) {
      this.instructionBanner.remove()
      this.instructionBanner = null
    }
  }

  private handleDocumentClick(event: MouseEvent) {
    console.log('FeedLet: handleDocumentClick called, isEnabled:', this.isEnabled)
    if (!this.isEnabled) return

    event.preventDefault()
    event.stopPropagation()

    // Ignore clicks on ExitBug elements
    const target = event.target as HTMLElement
    if (target.closest('[id^="feedlet"]')) {
      console.log('FeedLet: Ignoring click on ExitBug element')
      return
    }

    console.log('FeedLet: Creating marker at', event.clientX, event.clientY)
    this.createMarker(event)
  }

  private handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isEnabled) {
      this.disable()
    }
  }

  private createMarker(event: MouseEvent) {
    const target = event.target as HTMLElement
    const rect = target.getBoundingClientRect()
    const viewport = this.getViewportInfo()

    const marker: ContextualMarker = {
      id: `marker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x: event.clientX,
      y: event.clientY,
      element: target,
      elementSelector: this.getElementSelector(target),
      elementText: this.getElementText(target),
      timestamp: Date.now()
    }

    this.markers.set(marker.id, marker)
    this.renderMarker(marker)

    // Trigger callback
    if (this.onMarkerClick) {
      this.onMarkerClick(marker)
    }
  }

  private renderMarker(marker: ContextualMarker) {
    const markerElement = document.createElement('div')
    markerElement.className = 'feedlet-marker'
    markerElement.id = `feedlet-marker-${marker.id}`
    markerElement.style.left = `${marker.x}px`
    markerElement.style.top = `${marker.y}px`

    markerElement.addEventListener('click', (event) => {
      event.stopPropagation()
      if (this.onMarkerClick) {
        this.onMarkerClick(marker)
      }
    })

    document.body.appendChild(markerElement)
  }

  private getElementSelector(element: HTMLElement): string {
    if (element.id) {
      return `#${element.id}`
    }

    if (element.className) {
      const classes = element.className.split(' ').filter(c => c.length > 0)
      if (classes.length > 0) {
        return `${element.tagName.toLowerCase()}.${classes[0]}`
      }
    }

    // Try to create a more specific selector
    const path: string[] = []
    let current: HTMLElement | null = element

    while (current && current !== document.body) {
      let selector = current.tagName.toLowerCase()
      
      if (current.id) {
        selector += `#${current.id}`
        path.unshift(selector)
        break
      }

      if (current.className) {
        const classes = current.className.split(' ').filter(c => c.length > 0)
        if (classes.length > 0) {
          selector += `.${classes[0]}`
        }
      }

      path.unshift(selector)
      current = current.parentElement
    }

    return path.join(' > ')
  }

  private getElementText(element: HTMLElement): string {
    // Get the direct text content, limited to first 50 characters
    const text = element.textContent?.trim() || ''
    return text.length > 50 ? text.substring(0, 50) + '...' : text
  }

  getViewportInfo(): ViewportInfo {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY
    }
  }

  clearMarkers() {
    this.markers.forEach(marker => {
      const element = document.getElementById(`feedlet-marker-${marker.id}`)
      if (element) {
        element.remove()
      }
    })
    this.markers.clear()
  }

  getMarkers(): ContextualMarker[] {
    return Array.from(this.markers.values())
  }

  isContextualModeEnabled(): boolean {
    return this.isEnabled
  }
} 